import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import 'echarts-gl';
import { tubeSurface } from '../../../coreRuns3D';

// The Core Orientation tab's 3D view of the hole's composite run. Started as a
// copy of Line3DChart and deliberately kept separate from it, so this one can
// grow core-orientation features without touching the Charts tab or the
// Analysis page, which share the original.
//
// `tubes` draws solid cylinders: [{ from, to, radiusFraction, color, opacity }].
// The radius is a fraction of the plotted range rather than metres, because a
// true 96 mm hole would be invisible on a 50 m plot - the chart knows the range,
// so it does the sizing. The equal-range cube below is what keeps a tube's
// cross-section round; do not give the axes independent ranges.

// One shared empty default: a fresh [] per render would change the effect's
// dependency every time and redraw the chart on every parent render.
const NO_TUBES = [];

export const CoreOrientation3DChart = ({ title, xAxisName, yAxisName, zAxisName, seriesCollection, tubes = NO_TUBES }) => {
    // A ref rather than a hardcoded element id, as in Line3DChart.
    const container = useRef(null);

    const handleComponentLoaded = () => {
        var main = container.current;
        if (!main) {
            return;
        }
        var xValues = [];
        seriesCollection.forEach((series) => {
            series.points.forEach((point) => {
                xValues.push(point[0]);
            });
        });
        var yValues = [];
        seriesCollection.forEach((series) => {
            series.points.forEach((point) => {
                yValues.push(point[1]);
            });
        });
        var zValues = [];
        seriesCollection.forEach((series) => {
            series.points.forEach((point) => {
                zValues.push(point[2]);
            });
        });
        // A tube can reach past the lines - a rejected run deeper than every
        // approved one - so its ends count towards the extent too.
        tubes.forEach((tube) => {
            [tube.from, tube.to].forEach((point) => {
                xValues.push(point[0]);
                yValues.push(point[1]);
                zValues.push(point[2]);
            });
        });

        var min = Math.min(...xValues, ...yValues, ...zValues);
        var max = Math.max(...xValues, ...yValues, ...zValues);
        var range = max - min;
        var xRange = Math.max(...xValues) - Math.min(...xValues);
        var yRange = Math.max(...yValues) - Math.min(...yValues);
        var xAxisMin = Math.min(...xValues) - (range - xRange) / 2;
        var yAxisMin = Math.min(...yValues) - (range - yRange) / 2;
        var xAxisMax = xAxisMin + range;
        var yAxisMax = yAxisMin + range;

        // Build ground plane grid lines at z=0
        var groundLines = [];
        var gridStep = range / 10;

        // Lines along X axis (varying Y)
        for (var y = Math.ceil(yAxisMin / gridStep) * gridStep; y <= yAxisMax; y += gridStep) {
            groundLines.push({
                name: '',
                type: 'line3D',
                data: [[xAxisMin, y, 0], [xAxisMax, y, 0]],
                lineStyle: { width: 1, color: '#bbb', opacity: 0.6 },
                silent: true
            });
        }
        // Lines along Y axis (varying X)
        for (var x = Math.ceil(xAxisMin / gridStep) * gridStep; x <= xAxisMax; x += gridStep) {
            groundLines.push({
                name: '',
                type: 'line3D',
                data: [[x, yAxisMin, 0], [x, yAxisMax, 0]],
                lineStyle: { width: 1, color: '#bbb', opacity: 0.6 },
                silent: true
            });
        }

        // Axis lines through the origin
        var zMin = Math.floor(-range);
        var axisLines = [
            { name: xAxisName, type: 'line3D', data: [[Math.floor(xAxisMin), 0, 0], [Math.ceil(xAxisMax), 0, 0]], lineStyle: { width: 2, color: '#888' }, silent: true },
            { name: yAxisName, type: 'line3D', data: [[0, Math.floor(yAxisMin), 0], [0, Math.ceil(yAxisMax), 0]], lineStyle: { width: 2, color: '#888' }, silent: true },
            { name: zAxisName, type: 'line3D', data: [[0, 0, 0], [0, 0, zMin]], lineStyle: { width: 2, color: '#888' }, silent: true },
        ];

        // series.color pins the colour to the entity rather than to its index,
        // so filtering the collection cannot repaint the surviving series — and
        // a survey keeps the same colour here as in the plan and section views.
        var trajectorySeries = seriesCollection.map((series) => {
            return {
                name: series.name,
                type: 'line3D',
                data: series.points,
                ...(series.color ? { itemStyle: { color: series.color } } : {}),
                lineStyle: {
                    width: 3,
                    ...(series.color ? { color: series.color } : {}),
                    ...series.lineStyle
                }
            }
        });

        var tubeSeries = tubes
            .map((tube) => {
                var equation = tubeSurface(tube.from, tube.to, tube.radiusFraction * range);
                return equation && {
                    type: 'surface',
                    parametric: true,
                    parametricEquation: equation,
                    shading: 'lambert',
                    wireframe: { show: false },
                    itemStyle: { color: tube.color, opacity: tube.opacity ?? 1 },
                    silent: true
                };
            })
            .filter(Boolean);

        // Reuse the existing instance instead of initialising a second one on the
        // same node. Selecting surveys on and off re-runs this on every toggle,
        // and echarts.init would warn and leak a WebGL context each time.
        var chart = echarts.getInstanceByDom(main) || echarts.init(main);
        var option = {
            title: {
                text: title,
                left: 'center'
            },
            tooltip: {},
            toolbox: {
                feature: {
                    saveAsImage: {},
                    restore: {}
                }
            },
            xAxis3D: {
                min: Math.floor(xAxisMin),
                max: Math.ceil(xAxisMax),
                name: xAxisName,
                type: 'value',
                axisLabel: { show: false },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                splitArea: { show: false }
            },
            yAxis3D: {
                min: Math.floor(yAxisMin),
                max: Math.ceil(yAxisMax),
                name: yAxisName,
                type: 'value',
                axisLabel: { show: false },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                splitArea: { show: false }
            },
            zAxis3D: {
                max: 0,
                min: Math.floor(-range),
                name: zAxisName,
                type: 'value',
                axisLabel: { show: false },
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                splitArea: { show: false }
            },
            grid3D: {
                show: false,
                viewControl: {
                    autoRotate: false,
                    distance: 250,
                },
                // Only the tubes are shaded; lines ignore the light. Enough
                // ambient that the unlit side of a cylinder still shows its colour.
                light: {
                    main: { intensity: 1.2, alpha: 40, beta: 40 },
                    ambient: { intensity: 0.5 }
                }
            },
            // Tubes are drawn in the order given; the caller puts the
            // translucent hole last so it sits over the cores.
            series: [...groundLines, ...axisLines, ...trajectorySeries, ...tubeSeries]
        };
        // notMerge: echarts merges by series index, so without it a trajectory
        // removed from seriesCollection stays on the chart.
        chart.setOption(option, true);
    }

    useEffect(() => {
        handleComponentLoaded();
    }, [seriesCollection, tubes]); // eslint-disable-line react-hooks/exhaustive-deps -- handleComponentLoaded only reads these two props, both in deps

    // Release the WebGL context when the tab is left, rather than leaving the
    // instance bound to a detached node.
    useEffect(() => {
        const node = container.current;
        return () => {
            if (node) echarts.getInstanceByDom(node)?.dispose();
        };
    }, []);

    return (
        <div ref={container} style={{ width: '100%', height: 600 }}></div>
    )
}
